if (!customElements.get('mila-product-bar')) {
  customElements.define('mila-product-bar', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.info = this.product?.querySelector(`#ProductInfo-${this.dataset.section}`);
      this.form = this.product?.querySelector(`#product-form-${this.dataset.section}`);
      this.target = this.product?.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
      this.price = this.product?.querySelector('[data-bundle-price]') || this.product?.querySelector(`#price-${this.dataset.section}`);
      if (!this.target || !this.info || !('IntersectionObserver' in window)) return;
      this.syncVisibility = () => {
        this.visibilityFrame = null;
        // A jump can move the CTA from below to above without crossing an observer threshold.
        // Resolve the live CTA each time because variant updates may replace its DOM node.
        const target = this.product?.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const rect = target?.getBoundingClientRect();
        this.hidden = !rect || rect.height === 0 || rect.bottom > 0;
      };
      this.queueVisibility = () => {
        if (this.visibilityFrame == null) this.visibilityFrame = requestAnimationFrame(this.syncVisibility);
      };
      this.observer = new IntersectionObserver(this.queueVisibility);
      this.observer.observe(this.target);
      window.addEventListener('scroll', this.queueVisibility, {passive: true});
      window.addEventListener('resize', this.queueVisibility, {passive: true});
      this.queueVisibility();
      this.syncPrice = () => {
        const source = this.price?.matches('[data-bundle-price]') ? this.price : (this.price?.querySelector('.price__sale .price-item--sale') && this.price.querySelector('.price--on-sale')
          ? this.price.querySelector('.price__sale .price-item--sale')
          : this.price?.querySelector('.price__regular .price-item--regular'));
        if (source) this.querySelector('[data-mila-price]').textContent = source.textContent.trim();
      };
      this.syncPrice();
      if (this.price) {
        this.priceObserver = new MutationObserver(this.syncPrice);
        this.priceObserver.observe(this.price, {subtree: true, childList: true, characterData: true});
      }
      this.returnToOptions = (event) => {
        event.preventDefault();
        if (event.currentTarget.hasAttribute('data-mms-diffuser-sticky')) {
          const main = this.product?.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
          const appButton = this.product?.querySelector('.restock-rocket-preorder-button');
          const appPresent = !!appButton;
          const mainStyle = main ? window.getComputedStyle(main) : null;
          const mainVisible = main && main.getClientRects().length > 0 && mainStyle.display !== 'none' && mainStyle.visibility !== 'hidden';
          const incomplete = [...this.product.querySelectorAll('[data-bundle-oil]:not([disabled])')].some(select => !select.value);
          if (!appPresent && !incomplete && mainVisible && !main.disabled && main.getAttribute('aria-disabled') !== 'true' && !main.classList.contains('loading')) { main.click(); return; }
        }
        this.info.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
        const control = [...this.info.querySelectorAll('[data-bundle-oil]:not([disabled])')].find(select => !select.value) || this.info.querySelector('variant-selects select, variant-selects input:checked, select[name="selling_plan"], button[name="add"]:not([disabled])');
        if (control) control.focus({preventScroll: true});
      };
      this.querySelector('[data-mila-return]').addEventListener('click', this.returnToOptions);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.priceObserver?.disconnect();
      window.removeEventListener('scroll', this.queueVisibility);
      window.removeEventListener('resize', this.queueVisibility);
      if (this.visibilityFrame != null) cancelAnimationFrame(this.visibilityFrame);
      this.querySelector('[data-mila-return]')?.removeEventListener('click', this.returnToOptions);
    }
  });
}

// Legacy component contracts remain unchanged; native ECHO routing is separate.
// Prices and discounts are never changed client side.
function milaBundleItems(kind, variant, tier, oils, sellingPlan) {
  const limit = kind === 'pulse' ? 2 : kind === 'echo' ? 3 : 0;
  if (!Number.isInteger(tier) || tier < 1 || tier > limit || oils.length !== tier || !variant?.id || !variant.available || oils.some(oil => !oil.id || !oil.name)) {
    throw new Error('Please choose an available finish and each scent for your bundle.');
  }
  const offer = `${kind}-bundle-v1`;
  const pulse = kind === 'pulse';
  const tierName = pulse ? (tier === 1 ? 'Starter' : 'Most Popular') : `ECHO + ${tier} Fragrance ${tier === 1 ? 'Oil (120ml)' : 'Oils (120ml each)'}`;
  const properties = {
    _bundle_name: pulse ? `PULSE ${tierName} Bundle (${variant.title})` : `ECHO Bundle (${tierName})`,
    _bundle_scents: oils.map(oil => oil.name).join(', '),
    _fudge: JSON.stringify({offer, slot: 'diffuser', tier})
  };
  if (pulse) Object.assign(properties, {_bundle_tier: String(tier), _bundle_finish: variant.title});
  const diffuser = {id: Number(variant.id), quantity: 1, properties};
  if (sellingPlan) diffuser.selling_plan = sellingPlan;
  const items = [diffuser];
  oils.forEach(oil => {
    const existing = pulse && items.slice(1).find(item => item.id === Number(oil.id));
    if (existing) { existing.quantity += 1; return; }
    const oilProperties = {
      _bundle_parent: pulse ? 'PULSE HVAC Diffuser' : 'ECHO Cold-Air Diffuser',
      _fudge: JSON.stringify({offer, slot: 'oil', tier})
    };
    if (pulse) oilProperties._bundle_tier = String(tier);
    items.push({id: Number(oil.id), quantity: 1, properties: oilProperties});
  });
  return items;
}

// Native fixed bundles enforce the price and component inventory at Shopify checkout.
let milaEchoBundleMapPromise;
function milaEchoNativeBundleMap(url) {
  if (!url) return Promise.reject(new Error('Bundle information is unavailable. Please refresh and try again.'));
  if (!milaEchoBundleMapPromise) {
    milaEchoBundleMapPromise = fetch(url, {credentials: 'same-origin'})
      .then(response => { if (!response.ok) throw new Error('Bundle information could not load. Please refresh and try again.'); return response.json(); })
      .then(map => { if (map?.version !== 1 || String(map.productId) !== '15442012537080' || !map.variants) throw new Error('Bundle information is unavailable. Please contact us.'); return map; })
      .catch(error => { milaEchoBundleMapPromise = null; throw error; });
  }
  return milaEchoBundleMapPromise;
}
async function milaEchoNativeItems(url, variant, tier, oils, sellingPlan) {
  if (!Number.isInteger(tier) || tier < 1 || tier > 3 || oils.length !== tier || !variant?.id || !variant.available || oils.some(oil => !/^\d+$/.test(String(oil.id)) || !oil.name)) {
    throw new Error('Please choose an available finish and each scent for your bundle.');
  }
  if (sellingPlan) throw new Error('This ECHO bundle is a one time purchase. Please remove the subscription selection.');
  const map = await milaEchoNativeBundleMap(url);
  const key = String(variant.id) + '|' + oils.map(oil => String(oil.id)).sort((a,b) => Number(a) - Number(b)).join(',');
  const id = map.variants[key];
  if (!/^\d+$/.test(String(id || ''))) throw new Error('This scent combination is unavailable. Please choose another combination or contact us.');
  return [{id: Number(id), quantity: 1, properties: {Scents: oils.map(oil => oil.name).join(', '), _mms_echo_bundle: 'native-v1'}}];
}

// Fixed three oil bundles use exact approved native parents, never standalone oil additions.
const milaFixedBundleMaps = new Map();
async function milaFixedThreeItems(url, expectedProductId, variant, oils, sellingPlan) {
  if (!/^\d+$/.test(String(expectedProductId || '')) || !variant?.available || oils.length !== 3 || oils.some(oil => !/^\d+$/.test(String(oil.id)) || !oil.name)) throw new Error('Please choose all three available scents.');
  if (sellingPlan) throw new Error('This bundle preorder configuration needs updating. Please contact us.');
  if (!milaFixedBundleMaps.has(url)) milaFixedBundleMaps.set(url, fetch(url, {credentials: 'same-origin'}).then(response => { if (!response.ok) throw new Error('Bundle information could not load. Please try again.'); return response.json(); }).catch(error => { milaFixedBundleMaps.delete(url); throw error; }));
  const map = await milaFixedBundleMaps.get(url);
  if (map?.version !== 1 || String(map.productId) !== String(expectedProductId) || !map.variants) throw new Error('Bundle information is unavailable. Please contact us.');
  const key = String(variant.id) + '|' + oils.map(oil => String(oil.id)).sort((a,b) => Number(a)-Number(b)).join(',');
  const id = map.variants[key];
  if (!/^\d+$/.test(String(id || ''))) throw new Error('This scent combination is unavailable. Please choose another combination.');
  return [{id:Number(id),quantity:1,properties:{Scents:oils.map(oil=>oil.name).join(', '),'Included oils':'3 × 200ml selected oils replace the preset oil set',_mms_device_bundle:'native-v1'}}];
}

// Preserve native app line properties and cart attributes without copying form controls.
function milaBundleNativeData(formData) {
  const properties = Object.create(null);
  const attributes = Object.create(null);
  for (const [name, value] of formData.entries()) {
    if (typeof value !== 'string') continue;
    const match = /^(properties|attributes)\[([^\[\]]+)\]$/.exec(name);
    if (!match || ['__proto__', 'prototype', 'constructor'].includes(match[2])) continue;
    (match[1] === 'properties' ? properties : attributes)[match[2]] = value;
  }
  return {properties, attributes};
}

if (!customElements.get('mila-device-bundle')) {
  customElements.define('mila-device-bundle', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.form = this.product?.querySelector(`#${this.dataset.form}`);
      this.button = this.form?.querySelector('[type="submit"]');
      if (!this.form || !this.button) return;
      this.variants = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
      this.tierControl = this.querySelector('[data-bundle-tier]');
      this.error = this.querySelector('[data-bundle-error]');
      this.onTierChange = () => {
        const tier = Number(this.tierControl.value);
        this.querySelectorAll('[data-bundle-slot]').forEach(slot => {
          const active = Number(slot.dataset.bundleSlot) <= tier;
          slot.hidden = !active;
          const select = slot.querySelector('select');
          select.disabled = !active;
          select.required = active;
        });
        this.querySelector('[data-bundle-price]').textContent = this.tierControl.selectedOptions[0].dataset.price;
        this.error.hidden = true;
      };
      this.tierControl.addEventListener('change', this.onTierChange);
      this.onCardChange = (event) => {
        if (!event.target.matches('[data-bundle-card]')) return;
        this.tierControl.value = event.target.value;
        this.onTierChange();
      };
      this.addEventListener('change', this.onCardChange);
      if (this.dataset.pricingPending === 'true') {
        this.button.disabled = true;
        this.button.setAttribute('aria-disabled', 'true');
        const label = this.button.querySelector('span');
        if (label) label.textContent = 'Bundle checkout pending';
      }
      this.fixedStockState = this.form.querySelector('mms-preorder-state');
      this.fixedHardwareOut = this.fixedStockState?.dataset.oos === 'true';
      this.fixedHardwareAllowed = this.fixedStockState?.dataset.allowed === 'true';
      this.syncFixedParent = async () => {
        if (this.dataset.fixedThree !== 'true') return;
        const token = this.fixedSyncToken = (this.fixedSyncToken || 0) + 1;
        const input = this.form.querySelector('[name="id"]');
        input.disabled = true;
        input.value = '';
        const selects = [...this.querySelectorAll('[data-bundle-oil]')];
        if (selects.length !== 3 || selects.some(select => !select.value)) return;
        try {
          const source = this.variants.find(variant => String(variant.id) === this.dataset.sourceVariant);
          const oils = selects.map(select => ({id:select.value,name:select.selectedOptions[0].dataset.name}));
          const items = await milaFixedThreeItems(this.dataset.nativeBundleMap, this.dataset.nativeProductId, source, oils, null);
          if (token !== this.fixedSyncToken) return;
          input.value = String(items[0].id);
          input.disabled = false;
          if (this.fixedStockState) {
            const out = this.fixedHardwareOut || selects.some(select => select.selectedOptions[0]?.dataset.preorder === 'true');
            this.fixedStockState.dataset.variantId = String(items[0].id);
            this.fixedStockState.dataset.oos = String(out);
            this.fixedStockState.dataset.allowed = String(out && source.available && (!this.fixedHardwareOut || this.fixedHardwareAllowed));
            const notice = this.fixedStockState.querySelector('[data-preorder-notice]');
            if (notice) { notice.hidden = !out; if (out) notice.textContent = 'Preorder bundle. Delivery timing differs from in stock items. See any preorder details shown before ordering.'; }
            this.fixedStockState.sync?.();
          }
          this.form.dataset.productId = this.dataset.nativeProductId;
          input.dispatchEvent(new Event('change', {bubbles:true}));
        } catch (error) { if (token === this.fixedSyncToken) this.showError(error.message); }
      };
      this.onFixedOilChange = event => { if (event.target.matches('[data-bundle-oil]')) this.syncFixedParent(); };
      this.addEventListener('change', this.onFixedOilChange);
      this.syncFixedParent();
      this.onSubmit = this.submitBundle.bind(this);
      // Capture prevents Dawn from also adding the diffuser alone.
      this.form.addEventListener('submit', this.onSubmit, true);
    }
    async submitBundle(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.dataset.pricingPending === 'true') { this.showError('Bundle checkout is paused while we verify pricing.'); return; }
      if (this.pending || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
      this.error.hidden = true;
      const selects = [...this.querySelectorAll('[data-bundle-oil]:not([disabled])')];
      for (const select of selects) {
        if (!select.reportValidity()) return;
      }
      let items;
      let nativeData;
      this.pending = true;
      try {
        const data = new FormData(this.form);
        nativeData = milaBundleNativeData(data);
        const variantId = this.dataset.fixedThree === 'true' ? this.dataset.sourceVariant : data.get('id');
        const variant = this.variants.find(v => String(v.id) === String(variantId));
        const oils = selects.map(select => ({id: select.value, name: select.selectedOptions[0].dataset.name}));
        items = this.dataset.fixedThree === 'true'
          ? await milaFixedThreeItems(this.dataset.nativeBundleMap, this.dataset.nativeProductId, variant, oils, data.get('selling_plan'))
          : this.dataset.kind === 'echo'
          ? await milaEchoNativeItems(this.dataset.nativeBundleMap, variant, Number(this.tierControl.value), oils, data.get('selling_plan'))
          : milaBundleItems(this.dataset.kind, variant, Number(this.tierControl.value), oils, data.get('selling_plan'));
        if (this.dataset.fixedThree === 'true' || this.dataset.kind === 'echo') { const quantity = Number(data.get('quantity') || 1); if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Choose a valid bundle quantity.'); items[0].quantity = quantity; }
        if (this.dataset.fixedThree === 'true' && selects.some(select => select.selectedOptions[0]?.dataset.preorder === 'true')) nativeData.properties['Purchase type'] = 'Preorder';
        // Explicit bundle keys win; app preorder properties remain on the diffuser only.
        if (this.dataset.fixedThree === 'true') {
          const marker = nativeData.properties._stoq_shopify_selling_plan_id;
          if (marker) {
            const matchedOffer = (window._RestockRocketConfig?.sellingPlans || []).find(plan => plan.enabled && plan.use_shopify_selling_plan === false && plan.variant_ids?.map(String).includes(String(items[0].id)) && String(plan.shopify_selling_plan_id) === String(marker));
            if (!matchedOffer) throw new Error('This bundle preorder configuration needs updating. Please contact us.');
          }
        }
        items[0].properties = {...nativeData.properties, ...items[0].properties};
      } catch (error) { this.pending = false; this.showError(error.message); return; }
      this.pending = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('loading');
      const spinner = this.form.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = {items};
      if (Object.keys(nativeData.attributes).length) body.attributes = nativeData.attributes;
      if (cart) {
        body.sections = cart.getSectionsToRender().map(section => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      let added = false;
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify(body)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Your bundle could not be added. Please try again.');
        added = true;
        // A full cart navigation is the safe fallback if any returned section is unavailable.
        if (!cart || !result.sections || Object.values(result.sections).some(section => section === null)) {
          window.location.assign(window.routes.cart_url); return;
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'mila-device-bundle', productVariantId: items[0].id, cartData: result});
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } catch (error) {
        if (added) { window.location.assign(window.routes.cart_url); return; }
        this.showError(error.message || 'Your bundle could not be added. Please try again.');
      } finally {
        this.pending = false;
        this.button.removeAttribute('aria-disabled');
        this.button.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }
    disconnectedCallback() {
      this.tierControl?.removeEventListener('change', this.onTierChange);
      this.removeEventListener('change', this.onCardChange);
      this.removeEventListener('change', this.onFixedOilChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
    }
  });
}

